import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, input, output, signal, viewChild, DOCUMENT } from '@angular/core';
import { FormGroup, FormControl, Validators, FormArray, ReactiveFormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { AuthService } from '../../../auth.service';
import { IAuthVerified, IAuthResponse } from '../../../types';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { backend } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { from, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'auth-verify',
  templateUrl: './auth-verify.component.html',
  styleUrls: ['./auth-verify.component.scss'],
  standalone: true,
  imports: [MatIcon, MatButton, MatIconButton, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuthVerifyComponent {
  verifyCodeInputs = viewChild<ElementRef<HTMLDivElement>>('verifyCodeInputs');
  changes = inject(ChangeDetectorRef);
  document = inject(DOCUMENT);
  http = inject(HttpClient);
  toast = inject(ToastrService);
  destroy = inject(DestroyRef);

  state = output<'register' | 'request'>();
  login = output<IAuthVerified>();

  timer: NodeJS.Timeout | null = null;
  timerTill: DateTime = DateTime.now().plus({ minutes: 2 });
  timerValue = signal<string>('2:00');

  phone = input<string>();

  loading = signal<boolean>(false);

  form = new FormGroup({
    auth_id: new FormControl('', [Validators.required]),
    code: new FormControl('', [Validators.required, Validators.pattern(/^[0-9#]+$/)])
  });

  code: string = '';
  codeForm = new FormArray<FormControl<string | null>>([]);
  codeLength: number = 4;

  constructor(public ss: AuthService) {
    this.form.get('code')!.valueChanges.subscribe(code => {
      if (code && code.length > this.codeLength) {
        this.form.get('code')!.setValue(code.slice(0, this.codeLength));
        return;
      }
      this.code = code || '';
    });
  }

  focusElement(element: Element | EventTarget | null | undefined) {
    (element as HTMLElement)?.focus();
  }

  selectElement(element: Element | EventTarget | null | undefined) {
    (element as HTMLInputElement)?.select();
  }

  digitBuilder = () => {
    let index = this.codeForm.length;
    let control = new FormControl('', [Validators.required, Validators.pattern(/^[0-9#]+$/), Validators.maxLength(1), Validators.minLength(1)]);

    control.valueChanges.subscribe(value => {
      const pos = index + (control.invalid ? -1 : 1);

      if (!control.valid && value != '') {
        control.setValue('', { emitEvent: false });
        return;
      }

      this.focusElement(this.verifyCodeInputs()?.nativeElement?.children?.item(pos));

      setTimeout(() => {
        const codeForm = this.form.get('code')!;
        codeForm.setValue(this.codeForm.value.join(''));
        if (pos == 4 && codeForm.valid && !this.loading()) {
          this.confirmVerification();
        }
      }, 0);

    });

    return control;
  };

  onPaste(event: ClipboardEvent, index: number) {
    event.preventDefault();

    let pastedData = event.clipboardData?.getData('Text') || '';

    if (pastedData) {
      pastedData = pastedData.replace(/[^0-9#]/gm, '');
      for (let i = 0; i < pastedData.length; i++) {
        const digit = this.codeForm.at(index + i);
        if (digit) {
          setTimeout(() => {
            digit.setValue(pastedData[i]);
          }, i);
        } else {
          break;
        }
      }
    }
  }

  startTimer(seconds: number) {
    this.stopTimer();93
    this.timerTill = DateTime.now().plus({ seconds });
    this.timer = setInterval(() => {
      const timeRemaining = this.timerTill.diff(DateTime.now().minus({ millisecond: 200 }), ['minutes','seconds']).toFormat(`m:ss`);
      if (timeRemaining == '0:00' || timeRemaining.includes('-')) {
        this.stopTimer();
      }
      this.timerValue.set(timeRemaining);
    }, 1000);
  }

  stopTimer() {
    if (this.timer) {
      clearInterval(this.timer);
    }
    this.timer = null;
  }

  onWaitingForVerification(response: IAuthResponse, existingTimer?: number) {
    this.codeLength = response.code_length;

    while (this.codeForm.length > this.codeLength) {
      this.codeForm.removeAt(this.codeForm.length - 1);
    }

    while (this.codeForm.length < this.codeLength) {
      this.codeForm.push(this.digitBuilder());
    }

    this.changes.markForCheck();

    this.form.get('code')!.setValidators([Validators.required, Validators.pattern(/^[0-9#]+$/), Validators.maxLength(this.codeLength), Validators.minLength(4)]);

    this.form.reset({
      auth_id: response.auth_id,
      code: ''
    });

    setTimeout(() => {
      this.focusElement(this.verifyCodeInputs()?.nativeElement?.children?.item(0));
    }, 100);

    // this.registerForm.get('auth_id').setValue(response.auth_id);

    this.loading.set(false);
    if (!this.timer) {
      this.startTimer(existingTimer || 120);
    }
  }

  confirmVerification() {
    (this.document.activeElement as HTMLElement)?.blur();
    this.loading.set(true);

    const userVerify = this.form.value;

    from(this.ss.getIP()).pipe(switchMap(ip => this.http.post<IAuthVerified>(backend + 'auth/verify', userVerify, ip ? { headers: { cuip: ip } } : undefined)), takeUntilDestroyed(this.destroy)).subscribe({
      next: response => {
        this.stopTimer();
        this.login.emit(response);
        this.loading.set(false);
      },
      error: error => {
        error = (error.error || error);
        this.loading.set(false);

        switch (error.code) {
          case 4:
            this.state.emit('register');
            return;
          case 3:
            this.state.emit('request');
            break;
          case 2:
            this.codeForm.controls.forEach(digit => digit.setValue(''));
            setTimeout(() => {
              this.focusElement(this.verifyCodeInputs()?.nativeElement?.children?.item(0));
            }, 0);
            break;
        }

        this.toast.error(error.message || 'Unknown error occurred');

      }
    });

  }

  resendVerification() {
    this.loading.set(true);

    const auth_id = this.form.get('auth_id')!.value;

    if(auth_id) {
      from(this.ss.getIP()).pipe(switchMap(ip =>  this.http.post<IAuthResponse>(backend + 'auth/resend', { auth_id }, ip ? { headers: { cuip: ip } } : undefined)), takeUntilDestroyed(this.destroy)).subscribe({
        next: response => {
          this.toast.info('We have reattempted to send you an SMS with your verification code');
          this.onWaitingForVerification(response);
        },
        error: error => {
          error = (error.error || error);

          this.loading.set(false);
          switch (error.code) {
            case 12: {
              this.state.emit('request');
              break;
            }
          }
          this.toast.error(error.message || 'Unknown error occurred');
        }
      });

    } else {
      this.state.emit('request');
    }
  }

}
